import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogActions,
    Button,
    Checkbox,
    FormControlLabel,
    Switch
} from '@mui/material';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import updateQuestionAnswer from "app/Api Calls/UpdateQNA";
import { useDispatch } from "react-redux";
import {showMessage} from "store/messageSlice";

const EditQnADialog = ({ open, handleClose, qna, onSave }) => {
    const dispatch = useDispatch();
    const [title, setTitle] = useState(qna.title);
    const [question, setQuestion] = useState(qna.question);
    const [editorState, setEditorState] = useState(
        EditorState.createWithContent(ContentState.createFromBlockArray(htmlToDraft(qna.answer).contentBlocks))
    );
    const [isActive, setIsActive] = useState(qna.isActive === 1);
    const [isPublished, setIsPublished] = useState(qna.isPublished || false);

    const imageUploadCallback = (file) =>
        new Promise((resolve, reject) => {
            const currentHtml = draftToHtml(convertToRaw(editorState.getCurrentContent()));
            const currentLength = currentHtml.length;
            const imgTagOverhead = 100;
            const estimatedBase64Length = Math.ceil(file.size * 4 / 3);
            const totalEstimated = currentLength + imgTagOverhead + estimatedBase64Length;
            const MAX_ANSWER_LENGTH = 65535;

            if (totalEstimated > MAX_ANSWER_LENGTH) {
                dispatch(showMessage({
                    message: 'Η εικόνα είναι πολύ μεγάλη. Το συνολικό μέγεθος της απάντησης δεν μπορεί να υπερβαίνει τους 65.535 χαρακτήρες. Παρακαλώ επιλέξτε μικρότερη εικόνα.',
                    autoHideDuration: 5000,
                    anchorOrigin: { vertical: 'top', horizontal: 'center' },
                    variant: 'error',
                }));
                reject(new Error('Image exceeds size limit'));
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => resolve({ data: { link: e.target.result } });
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });

    const handleSave = async () => {
        const answerHtml = draftToHtml(convertToRaw(editorState.getCurrentContent()));

        if (answerHtml.length > 65535) {
            dispatch(showMessage({
                message: 'Η απάντηση υπερβαίνει τους 65.535 χαρακτήρες. Παρακαλώ μειώστε το κείμενο ή τις εικόνες.',
                autoHideDuration: 5000,
                anchorOrigin: { vertical: 'top', horizontal: 'center' },
                variant: 'error',
            }));
            return;
        }

        const updatedQnA = {
            ...qna,
            title,
            question,
            answer: answerHtml,
            isActive: isActive ? 1 : 0,
            isPublished
        };

        try {
            await updateQuestionAnswer(updatedQnA);
            console.log('QnA updated successfully');
            onSave(updatedQnA); // You might want to refresh or update the parent component's state here
        } catch (error) {
            console.error('Failed to update QnA', error);
            // Handle the error appropriately
        }

        handleClose();
    };


    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth={true}>
            <DialogTitle>Επεξεργασία Ερώτησης/Απάντησης</DialogTitle>
            <DialogContent>
                <TextField
                    margin="dense"
                    label="Τίτλος"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                />
                <TextField
                    margin="dense"
                    label="Ερώτηση"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                />
                <div style={{ border: '1px solid #F1F1F1', padding: '5px', minHeight: '200px', marginTop: '20px' }}>
                    <Editor
                        editorState={editorState}
                        onEditorStateChange={setEditorState}
                        wrapperClassName="demo-wrapper"
                        editorClassName="demo-editor"
                        toolbar={{
                            options: ['inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign', 'history', 'image'],
                            inline: {
                                options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace'],
                                bold: { className: 'bordered-option-classname' },
                                italic: { className: 'bordered-option-classname' },
                                underline: { className: 'bordered-option-classname' },
                                strikethrough: { className: 'bordered-option-classname' },
                                monospace: { className: 'bordered-option-classname' },
                            },
                            blockType: {
                                className: 'bordered-option-classname',
                                dropdownClassName: 'dropdown-classname',
                            },
                            fontSize: { className: 'bordered-option-classname' },
                            fontFamily: {
                                options: ['Arial', 'Georgia', 'Impact', 'Tahoma', 'Times New Roman', 'Verdana'],
                                className: 'bordered-option-classname',
                                dropdownClassName: 'dropdown-classname',
                            },
                            list: {
                                inDropdown: false,
                                className: 'bordered-option-classname',
                                dropdownClassName: 'dropdown-classname',
                                options: ['unordered', 'ordered', 'indent', 'outdent'],
                            },
                            textAlign: {
                                inDropdown: false,
                                className: 'bordered-option-classname',
                                dropdownClassName: 'dropdown-classname',
                                options: ['left', 'center', 'right', 'justify'],
                            },
                            history: {
                                inDropdown: false,
                                className: 'bordered-option-classname',
                                dropdownClassName: 'dropdown-classname',
                                options: ['undo', 'redo'],
                            },
                            image: {
                                uploadCallback: imageUploadCallback,
                                alt: { present: false, mandatory: false },
                                previewImage: true,
                                inputAccept: 'image/gif,image/jpeg,image/jpg,image/png,image/svg,image/webp',
                                defaultSize: {
                                    height: 'auto',
                                    width: 'auto',
                                },
                            },
                        }}
                    />
                </div>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                            name="isActive"
                            color="primary"
                        />
                    }
                    label="Ενεργό"
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={isPublished}
                            onChange={(e) => setIsPublished(e.target.checked)}
                            name="isPublished"
                            color="primary"
                        />
                    }
                    label="Δημοσιευμένο"
                />

            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Άκυρο</Button>
                <Button onClick={handleSave}>Αποθήκευση</Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditQnADialog;
